import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { deleteObject, getDownloadURL, ref, uploadBytes, uploadBytesResumable } from 'firebase/storage';
import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db, storage } from '@/lib/firebase';
import { Button } from '@/components/ui/Button';
import { toast } from '@/components/ui/Toast';
import {
  PROFILE_GALLERY_MAX_VIDEOS,
  SERVICE_VIDEO_MAX_SECONDS,
  type ProfileGalleryVideo,
} from '@makhe/shared';
import {
  VIDEO_ACCEPT,
  assertShortVideo,
  captureVideoPoster,
  safeStorageFileName,
} from '@/lib/video';

function newId() {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

interface Props {
  uid: string;
  videos: ProfileGalleryVideo[] | undefined;
}

export function ProfileReelEditor({ uid, videos }: Props) {
  const list = videos ?? [];
  const fileRef = useRef<HTMLInputElement>(null);
  const replaceIdRef = useRef<string | null>(null);
  const [draft, setDraft] = useState<{ file: File; preview: string; duration: number } | null>(
    null,
  );
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);

  useEffect(() => {
    if (window.location.hash === '#reel') {
      document.getElementById('reel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  useEffect(() => {
    return () => {
      if (draft?.preview) URL.revokeObjectURL(draft.preview);
    };
  }, [draft?.preview]);

  const pick = async (file: File | undefined, replaceId: string | null) => {
    if (!file) return;
    try {
      const duration = await assertShortVideo(file, SERVICE_VIDEO_MAX_SECONDS);
      if (draft?.preview) URL.revokeObjectURL(draft.preview);
      replaceIdRef.current = replaceId;
      setDraft({ file, preview: URL.createObjectURL(file), duration });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };

  const persist = async (next: ProfileGalleryVideo[]) => {
    await updateDoc(doc(db, 'users', uid), {
      'sellerProfile.galleryVideos': next,
      updatedAt: serverTimestamp(),
    });
  };

  const publish = async () => {
    if (!draft) return;
    const replacing = replaceIdRef.current;
    if (!replacing && list.length >= PROFILE_GALLERY_MAX_VIDEOS) {
      toast.error(`Up to ${PROFILE_GALLERY_MAX_VIDEOS} videos on your profile.`);
      return;
    }
    setBusy(true);
    setProgress(0);
    const id = replacing ?? newId();
    const storagePath = `users/${uid}/gallery/${id}-${safeStorageFileName(draft.file.name)}`;
    try {
      const task = uploadBytesResumable(ref(storage, storagePath), draft.file);
      const url = await new Promise<string>((resolve, reject) => {
        task.on(
          'state_changed',
          (snap) => setProgress((snap.bytesTransferred / snap.totalBytes) * 100),
          reject,
          () => getDownloadURL(task.snapshot.ref).then(resolve, reject),
        );
      });

      let thumbnailURL: string | undefined;
      let thumbnailStoragePath: string | undefined;
      try {
        const poster = await captureVideoPoster(draft.file);
        if (poster) {
          thumbnailStoragePath = `users/${uid}/gallery/${id}-poster.jpg`;
          await uploadBytes(ref(storage, thumbnailStoragePath), poster, {
            contentType: 'image/jpeg',
          });
          thumbnailURL = await getDownloadURL(ref(storage, thumbnailStoragePath));
        }
      } catch {
        /* poster is optional */
      }

      const item: ProfileGalleryVideo = {
        id,
        url,
        thumbnailURL,
        storagePath,
        thumbnailStoragePath,
        durationSec: Math.round(draft.duration),
        createdAtMs: Date.now(),
      };

      const previous = replacing ? list.find((v) => v.id === replacing) : undefined;
      const next = replacing
        ? list.map((v) => (v.id === replacing ? item : v))
        : [...list, item];
      await persist(next);

      if (previous) {
        void deleteObject(ref(storage, previous.storagePath)).catch(() => null);
        if (previous.thumbnailStoragePath) {
          void deleteObject(ref(storage, previous.thumbnailStoragePath)).catch(() => null);
        }
      }

      URL.revokeObjectURL(draft.preview);
      setDraft(null);
      replaceIdRef.current = null;
      toast.success(replacing ? 'Clip replaced.' : 'Added to your profile.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const remove = async (clip: ProfileGalleryVideo) => {
    if (busy) return;
    setBusy(true);
    try {
      await persist(list.filter((v) => v.id !== clip.id));
      void deleteObject(ref(storage, clip.storagePath)).catch(() => null);
      if (clip.thumbnailStoragePath) {
        void deleteObject(ref(storage, clip.thumbnailStoragePath)).catch(() => null);
      }
      toast.success('Removed.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="reel" className="mt-6">
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="eyebrow mb-1">Your reel</p>
          <h2 className="font-display text-2xl">Show the work</h2>
          <p className="mt-1 text-sm text-makhe-muted">
            Buyers see this on your public profile before they book. Up to{' '}
            {PROFILE_GALLERY_MAX_VIDEOS} clips, {SERVICE_VIDEO_MAX_SECONDS}s each.
          </p>
        </div>
        <Link to={`/seller/${uid}`} className="shrink-0 text-xs text-makhe-primary underline">
          View as buyers see you
        </Link>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept={VIDEO_ACCEPT}
        hidden
        data-testid="profile-reel-file"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          void pick(file, replaceIdRef.current);
        }}
      />

      {draft && (
        <div className="mt-4 rounded-xl border border-makhe-brand/25 bg-makhe-brand/5 p-4">
          <p className="text-[11px] uppercase tracking-widest text-makhe-muted">Preview</p>
          <video
            src={draft.preview}
            controls
            playsInline
            className="mt-3 max-h-72 w-full rounded-lg bg-black"
          />
          <p className="mt-2 text-xs text-makhe-muted">{Math.round(draft.duration)}s</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button type="button" size="sm" loading={busy} onClick={() => void publish()}>
              {replaceIdRef.current ? 'Replace on profile' : 'Add to profile'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => {
                URL.revokeObjectURL(draft.preview);
                setDraft(null);
                replaceIdRef.current = null;
              }}
            >
              Cancel
            </Button>
          </div>
          {progress != null && (
            <p className="mt-2 text-xs text-makhe-muted">{Math.round(progress)}% uploaded</p>
          )}
        </div>
      )}

      <div className="mt-4 flex gap-3 overflow-x-auto pb-2">
        {list.map((clip) => (
          <article
            key={clip.id}
            data-testid="profile-reel-clip"
            className="w-28 shrink-0 overflow-hidden rounded-xl border border-makhe-line bg-makhe-paper"
          >
            <video
              src={clip.url}
              poster={clip.thumbnailURL}
              muted
              playsInline
              className="aspect-[9/16] w-full object-cover"
            />
            <div className="flex flex-col gap-1 p-2">
              <button
                type="button"
                className="text-left text-[10px] uppercase tracking-widest text-makhe-primary"
                onClick={() => {
                  replaceIdRef.current = clip.id;
                  fileRef.current?.click();
                }}
              >
                Replace
              </button>
              <button
                type="button"
                className="text-left text-[10px] uppercase tracking-widest text-makhe-muted"
                onClick={() => void remove(clip)}
              >
                Delete
              </button>
            </div>
          </article>
        ))}
        {list.length < PROFILE_GALLERY_MAX_VIDEOS && !draft && (
          <button
            type="button"
            className="flex aspect-[9/16] w-28 shrink-0 flex-col items-center justify-center rounded-xl border border-dashed border-makhe-line text-center text-[11px] uppercase tracking-widest text-makhe-muted"
            onClick={() => {
              replaceIdRef.current = null;
              fileRef.current?.click();
            }}
          >
            Add clip
          </button>
        )}
      </div>
    </section>
  );
}
